import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Typography,
  Grid,
  Chip,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  TextField,
} from "@mui/material";
import {
  CheckBox as CheckBoxIcon,
  Event as EventIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  ArrowDropDownCircle as ArrowDropDownIcon,
  ChecklistRtl as ChecklistRtlIcon,
  AttachFile as AttachFileIcon,
  Draw as DrawIcon,
  PhotoCamera as PhotoCameraIcon,
  Star as StarIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Notes as NotesIcon,
  Title as TitleIcon,
} from "@mui/icons-material";
import {
  ITEM_TYPES,
  ITEM_TYPE_CONFIGS,
  getItemTypesByCategory,
} from "../../constants/itemTypes";

/**
 * Enhanced gallery component for selecting item types with categorization
 */
const ItemTypeGallery = ({ onAddItem, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentStep, setCurrentStep] = useState("selection"); // "selection" or "configuration"
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [answerLists, setAnswerLists] = useState({});
  const [selectedList, setSelectedList] = useState("");
  const [numericMin, setNumericMin] = useState({ error: false, value: "" });
  const [numericMax, setNumericMax] = useState({ error: false, value: "" });
  const [numericStep, setNumericStep] = useState({ error: false, value: "" });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Load answer lists on component mount
  useEffect(() => {
    const loadAnswerLists = async () => {
      try {
        const response = await fetch("/answers_lists_denmark.json");
        const data = await response.json();
        setAnswerLists(data);
      } catch (error) {
        console.error("Failed to load answer lists:", error);
      }
    };
    loadAnswerLists();
  }, []);

  const handleOpen = () => {
    setOpen(true);
    setCurrentStep("selection");
    setSelectedItemType(null);
    setSelectedList("");
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentStep("selection");
    setSelectedItemType(null);
    setSelectedList("");
  };

  const handleSelectItemType = (itemType) => {
    if (itemType === ITEM_TYPES.SELECT || itemType === ITEM_TYPES.NUMBER) {
      // For SELECT items, go to configuration step
      setSelectedItemType(itemType);
      setCurrentStep("configuration");
    } else {
      // For other items, add directly
      onAddItem(itemType);
      setOpen(false);
    }
  };

  const handleBackToSelection = () => {
    setCurrentStep("selection");
    setSelectedItemType(null);
    setSelectedList("");
  };

  const handleConfigurationComplete = () => {
    if (selectedItemType === ITEM_TYPES.SELECT && selectedList) {
      // Pass the item type and configuration data
      onAddItem(selectedItemType, {
        selectedList,
        options: answerLists[selectedList] || [],
      });
      setOpen(false);
    }

    if (selectedItemType === ITEM_TYPES.NUMBER) {
      // Pass the item type and configuration data
      onAddItem(selectedItemType, {
        min: numericMin.value || "",
        max: numericMax.value || "",
        step: numericStep.value || "",
      });
      setOpen(false);
    }
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const getIcon = (iconName) => {
    const iconMap = {
      CheckBox: <CheckBoxIcon fontSize="large" />,
      Event: <EventIcon fontSize="large" />,
      TextFields: <TextFieldsIcon fontSize="large" />,
      Numbers: <NumbersIcon fontSize="large" />,
      ArrowDropDown: <ArrowDropDownIcon fontSize="large" />,
      ChecklistRtl: <ChecklistRtlIcon fontSize="large" />,
      AttachFile: <AttachFileIcon fontSize="large" />,
      Draw: <DrawIcon fontSize="large" />,
      PhotoCamera: <PhotoCameraIcon fontSize="large" />,
      Star: <StarIcon fontSize="large" />,
      Notes: <NotesIcon fontSize="large" />,
      Header: <TitleIcon fontSize="large" />,
    };
    return iconMap[iconName] || <CheckBoxIcon fontSize="large" />;
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      basic: "primary",
      composite: "secondary",
      selection: "info",
      media: "warning",
      assessment: "success",
    };
    return colorMap[category] || "default";
  };

  const itemTypesByCategory = getItemTypesByCategory();
  const categories = Object.keys(itemTypesByCategory);

  const getFilteredItemTypes = () => {
    if (selectedCategory === "all") {
      return Object.values(ITEM_TYPE_CONFIGS);
    }
    return itemTypesByCategory[selectedCategory] || [];
  };

  const filteredItemTypes = getFilteredItemTypes();

  // Configuration interface for SelectItem
  const renderSelectConfiguration = () => {
    const listKeys = Object.keys(answerLists);

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Configure Dropdown Select
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose which option list to use for this dropdown select item
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="answer-list-select-label">
            Select Option List
          </InputLabel>
          <Select
            labelId="answer-list-select-label"
            value={selectedList}
            label="Select Option List"
            onChange={(e) => setSelectedList(e.target.value)}
          >
            {listKeys.map((listKey) => (
              <MenuItem key={listKey} value={listKey}>
                {listKey} ({answerLists[listKey]?.length || 0} options)
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedList && answerLists[selectedList] && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Preview Options:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                maxHeight: 200,
                overflow: "auto",
                bgcolor: "grey.50",
              }}
            >
              <List dense>
                {answerLists[selectedList].map((option, index) => (
                  <ListItem
                    key={index}
                    divider={index < answerLists[selectedList].length - 1}
                  >
                    <ListItemText
                      primary={option || "(Empty option)"}
                      primaryTypographyProps={{
                        variant: "body2",
                        color: option ? "text.primary" : "text.secondary",
                        fontStyle: option ? "normal" : "italic",
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

  const renderNumericItemConfiguration = () => {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Configure numeric field item
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose the minimum and the maximum value possible for the field.
          Choose how many decimal places do you want to save.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <TextField
              fullWidth
              required
              variant="outlined"
              label="MIN"
              value={numericMin.value}
              onChange={(e) => {
                if (!e.target.value || e.target.value === "") {
                  setNumericMin({ error: false, value: e.target.value });
                  return;
                }
                const newValue = parseFloat(e.target.value);
                setNumericMin({
                  error: isNaN(newValue),
                  value: e.target.value,
                });
              }}
              size="small"
              error={numericMin.error}
              helperText={numericMin.error ? "Insert numeric value" : ""}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <TextField
              fullWidth
              required
              variant="outlined"
              label="MAX"
              value={numericMax.value}
              onChange={(e) => {
                if (!e.target.value || e.target.value === "") {
                  setNumericMax({ error: false, value: e.target.value });
                  return;
                }
                const newValue = parseFloat(e.target.value);
                setNumericMax({
                  error: isNaN(newValue),
                  value: e.target.value,
                });
                return;
              }}
              size="small"
              error={numericMax.error}
              helperText={numericMax.error ? "Insert numeric value" : ""}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 4 }}>
            <TextField
              fullWidth
              required
              variant="outlined"
              label="STEP"
              value={numericStep.value}
              onChange={(e) => {
                if (!e.target.value || e.target.value === "") {
                  setNumericStep({ error: false, value: e.target.value });
                  return;
                }
                const newValue = parseFloat(e.target.value);
                setNumericStep({
                  error: isNaN(newValue),
                  value: e.target.value,
                });
                return;
              }}
              size="small"
              error={numericStep.error}
              helperText={numericStep.error ? "Insert numeric value" : ""}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderConfiguration = () => {
    if (selectedItemType === ITEM_TYPES.SELECT) {
      return renderSelectConfiguration();
    }

    if (selectedItemType === ITEM_TYPES.NUMBER) {
      return renderNumericItemConfiguration();
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        disabled={disabled}
        fullWidth={isMobile}
        sx={{ mb: 2 }}
      >
        Add Item
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {currentStep === "configuration" && (
              <IconButton
                onClick={handleBackToSelection}
                size="small"
                sx={{ mr: 1 }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box>
              <Typography variant="h5" component="div">
                {currentStep === "selection"
                  ? "Choose Item Type"
                  : "Configure Item"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentStep === "selection"
                  ? "Select the type of item you want to add to your template"
                  : "Configure the selected item type"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {currentStep === "selection" ? (
            <>
              {/* Category Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
                {/* <Tabs
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="item type categories"
                >
                  <Tab label="All" value="all" />
                  {categories.map((category) => (
                    <Tab
                      key={category}
                      label={
                        category.charAt(0).toUpperCase() + category.slice(1)
                      }
                      value={category}
                    />
                  ))}
                </Tabs> */}
              </Box>

              {/* Item Type Grid */}
              <Grid container spacing={2}>
                {filteredItemTypes.map((itemType) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={itemType.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        textAlign: "center",
                        cursor: "pointer",
                        border: "2px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          borderColor: "primary.main",
                          bgcolor: "action.hover",
                          transform: "translateY(-2px)",
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleSelectItemType(itemType.id)}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          color: "primary.main",
                          mb: 2,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        {getIcon(itemType.icon)}
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h6"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {itemType.name}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.4,
                          flexGrow: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {itemType.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {filteredItemTypes.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No item types found in this category.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            /* Configuration Step */
            renderConfiguration()
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          {currentStep === "selection" ? (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1 }}
              >
                {filteredItemTypes.length} item type
                {filteredItemTypes.length !== 1 ? "s" : ""} available
              </Typography>
              <Button onClick={handleClose} color="inherit">
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ flexGrow: 1 }}
              >
                Configure your dropdown select item
              </Typography>
              <Button onClick={handleBackToSelection} color="inherit">
                Back
              </Button>
              <Button
                onClick={handleConfigurationComplete}
                variant="contained"
                // disabled={() => {
                //   if (selectedItemType === ITEM_TYPES.SELECT) {
                //     return !selectedList ? true : false;
                //   }

                //   if (selectedItemType === ITEM_TYPES.NUMBER) {
                //     return !numericMin.value ||
                //       !numericMax.value ||
                //       !numericStep.value
                //       ? true
                //       : false;
                //   }

                //   return false;
                // }}
              >
                Add Item
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemTypeGallery;
